import { useState, type ReactNode } from 'react';

import { ApiError, api } from '../../api';
import { go } from '../../App';
import { config } from '../../config';
import { useLoad, usePolled } from '../../data';
import { ConnectionState, InkHero, Loading, Panel, ago, fmtMoney, formatWhen, summariseDays, zoneDate } from '../../ui';
import { Account, AvailabilityPattern, Booking, Connection, Onboarding, Product, TripSuggestion } from '../../types';
import { AnswerButton, TripRow, rowFocusId, useAnswers, type AnswerKind } from './answers';
import {
  ACCEPT_LINE,
  ANOTHER_HOTEL,
  TRIP_SUGGESTION,
  tripPreview,
  CONNECT_TASK_META,
  HOTELS_UNREAD,
  SET_UP_FOR_YOU,
  canBookNow,
  connectedWith,
  finishFoot,
  finishFootListed,
  MY_TRIPS_LINK,
  PHONE_APP,
  tripRowOffered,
  hotelName,
  standingOf,
  todayStanding,
} from '../../joining';

// The first-run state of Today (canon 11 E2 step 6): while a supplier is still
// onboarding, the home IS their setup checklist — the three E2 steps and the
// "Finish — I'm open for work" that opens them for work. Reuses the registered
// Business profile / My trips / Availability Forms (George's S11 ruling — no new
// screen), each reached one tap away, and finishes through POST /onboarding/complete.
const SETUP_STEPS: { key: 'basics' | 'product' | 'availability'; title: string; desc: string; to: string }[] = [
  { key: 'basics', title: 'Your business', desc: 'Name, what you do, where you leave from, languages.', to: '/profile' },
  { key: 'product', title: 'Your first trip', desc: 'One trip or transfer, and what you are paid for it.', to: '/trips' },
  { key: 'availability', title: 'When you work', desc: 'Your working days, hours and season.', to: '/availability' },
];

// `waiting` are the hotels with a pending connection. During setup none can be
// accepted (B33 — the API refuses it): the hotel that invited them goes live at
// Finish, and one that asked any other way becomes a Connect task on the
// ordinary Today afterwards. So they are named here as waiting, with nothing to
// press — the phone's first run says the same. The line promises only the
// supplier's own step, which is true for both kinds of hotel; this screen
// cannot tell them apart.
function OnboardingHome({
  ob,
  waiting,
  answered,
  onAnswer,
  afterFinish,
  afterKeep,
}: {
  ob: Onboarding;
  waiting: Connection[];
  // N42: Not now on a request (or another hotel's invitation) during setup too,
  // and the lines of the answers just given, with their undo.
  answered: ReactNode;
  onAnswer: (kind: AnswerKind, c: Connection) => Promise<void>;
  afterFinish: () => Promise<void>;
  // The onboarding state the Keep answered: Today moves on with it.
  afterKeep: (state: Onboarding) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = SETUP_STEPS.filter((s) => ob.steps[s.key]).length;
  // N42: the hotel Finish connects — the one they said yes to — never one that
  // asked, which still needs their Accept.
  const joinedThrough = waiting.find((c) => c.via !== 'request' && c.via !== 'invited');

  // DECISION-080 §9 — an account someone else set up opens on what was set up,
  // price first, until the supplier keeps it.
  if (ob.setUpForYou && !ob.setUpForYou.reviewed) return <SetUpForYou onKept={afterKeep} />;

  const finish = async () => {
    setBusy(true);
    setError('');
    try {
      await api('POST', '/onboarding/complete');
      await afterFinish();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div>
      <InkHero eyebrow="Welcome" figure={`${done}/3`} unit="done" explain="A few steps and you're open for work." />

      {answered}
      {waiting.length > 0 && (
        <Panel title="Waiting for you" meta={`${waiting.length} hotel${waiting.length === 1 ? '' : 's'}`} flush>
          <table>
            <tbody>
              {waiting.map((c) => {
                const name = c.name ?? c.hotelName ?? 'A hotel';
                // N42: a hotel that asked (or invited the number) can be said not
                // now to during setup; the hotel they said yes to cannot.
                const asked = c.via === 'request' || c.via === 'invited';
                return (
                  <tr key={c.hotelAccountId ?? c.accountId}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <ConnectionState kind="pending" label="Waiting for you to finish" />
                    </td>
                    <td id={rowFocusId(c)} tabIndex={-1}>
                      {name}
                      <br />
                      <span className="hint">{asked ? ANOTHER_HOTEL.waitsForFinish(name) : `Finish setting up below — ${name} is waiting for you.`}</span>
                    </td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      {asked && <AnswerButton kind="not-now" c={c} onAnswer={onAnswer} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title="Set up your business" meta={`${done} of 3`} flush>
        <table>
          <tbody>
            {SETUP_STEPS.map((s) => {
              const isDone = ob.steps[s.key];
              // N39: a first trip still to add opens the trip form itself, which
              // starts from the inviting hotel's trip when there is one.
              const to = s.key === 'product' && !isDone ? '/trips/new' : s.to;
              const offered = s.key === 'product' && !isDone ? ob.invitedTrip : undefined;
              return (
                <tr key={s.key} className="click" onClick={() => go(to)}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={`uchip ${isDone ? 'waiting' : 'soon'}`}>{isDone ? 'Done' : 'To do'}</span>
                  </td>
                  <td>
                    {s.title}
                    <br />
                    <span className="hint">{offered ? tripRowOffered(offered.hotelName, offered.trip.name) : s.desc}</span>
                  </td>
                  <td className="num"><span className="open">{isDone ? 'Edit →' : 'Do this →'}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>

      {error && <p className="error-line" role="alert">{error}</p>}

      <div className="form-foot">
        {/* B35: Finish opens them for work; a hotel can book them only once it
            has listed a trip, so the line promises only that. */}
        <span className="hint" style={{ flex: 1, alignSelf: 'center' }}>
          {ob.complete && ob.invitedTrip?.listsAtFinish
            ? finishFootListed(ob.invitedTrip.hotelName, ob.invitedTrip.trip.name)
            : finishFoot(ob.complete, joinedThrough ? hotelName(joinedThrough) : undefined)}
        </span>
        <button className="btn" onClick={finish} disabled={busy || !ob.complete} style={{ flex: 'none', minWidth: 200 }}>
          {busy ? 'Opening…' : "Finish — I'm open for work"}
        </button>
      </div>
    </div>
  );
}

// The supplier home. The hero owns today's single most important fact: the next
// job, or the good news that nothing needs them. Beneath it is **Eva's task
// list** (§4.3) — the cross-module "what goes wrong soonest" queue, built as a
// Today section (canon 09's register is closed at 41; "Eva tasks" is prose, not
// a 42nd screen). It folds the reads Today already polls — waiting requests and
// connection requests — into one prioritised list, each row carrying its
// existing one-tap target. A deterministic fold (the same discipline as the
// rest of Eva's core); a model-written ordering is the faithful upgrade.

// One actionable item across modules. `accept` rows carry the connection so the
// row can approve it inline through the SAME POST the My hotels screen uses.
interface Task {
  key: string;
  severity: number; // lower = more urgent
  chipClass: 'escalated' | 'soon' | 'waiting';
  chipText: string;
  title: string;
  meta: string;
  go?: () => void;
  connection?: Connection;
  // N42: a live hotel's trip, offered as a suggestion beside theirs.
  trip?: { c: Connection; s: TripSuggestion };
  // N42 fix round: the view's one primary — the first hotel's Accept.
  primary?: boolean;
}

export function Today() {
  const { data, error } = usePolled<{ items: Booking[] }>('/bookings');
  const connections = usePolled<{ items: Connection[] }>('/connections');
  const account = useLoad<Account>('/account');
  const loadedOnboarding = useLoad<Onboarding>('/onboarding');
  // DECISION-080 §9: the supplier's Keep answers the new onboarding state, and
  // Today moves on with that answer rather than a second read — while the
  // loaded one still predates the Keep.
  const [kept, setKept] = useState<Onboarding | null>(null);
  const loaded = loadedOnboarding.data;
  const onboarding = {
    ...loadedOnboarding,
    data: kept && loaded?.setUpForYou && !loaded.setUpForYou.reviewed ? kept : loaded,
  };
  const items = data ? data.items : null;
  // N42: Not now (and its undo toast) said here — the hotel it answered leaves the
  // list while its window is open (N42 fix round: the design system's toast).
  const { answer, isHidden, toast } = useAnswers(connections.reload, connections.data?.items);
  const [tripSaid, setTripSaid] = useState<string[]>([]);
  const pendingConns = (connections.data?.items ?? []).filter((c) => c.status === 'pending' && !isHidden(c));
  const answeredLines = toast;
  // The accepted connection's card vanishes with the reload — this line is
  // the toast-shaped acknowledgement that names what changed.
  const [connectedName, setConnectedName] = useState('');

  // "Today" in the property's day — never the device's, never UTC. At
  // 01:00 Athens, a 09:00 job is today, whatever UTC says.
  const today = zoneDate(account.data?.timezone ?? 'Europe/Athens', Date.now());
  const jobsToday = (items ?? [])
    .filter((b) => b.status === 'confirmed' && b.start.slice(0, 10) === today)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const waiting = (items ?? []).filter((b) => b.status === 'pending' || b.status === 'requested');
  const upcoming = (items ?? [])
    .filter((b) => b.status === 'confirmed' && b.start.slice(0, 10) > today)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
    .slice(0, 5);

  // The fold: every pending item, most-urgent first (canon 11 F3's spirit —
  // escalated, then starting soon, then oldest waiting; connection asks sit
  // just under the escalated line, above ordinary waits).
  const tasks: Task[] = [];
  const soonMs = Date.now() + 24 * 3600_000;
  for (const b of waiting) {
    const esc = b.chase?.state === 'escalated';
    const starts = Date.parse(b.start);
    const soon = starts > Date.now() && starts < soonMs;
    tasks.push({
      key: `req-${b.id}`,
      severity: esc ? 0 : soon ? 1 : 3,
      chipClass: esc ? 'escalated' : soon ? 'soon' : 'waiting',
      chipText: esc ? 'Answer now' : soon ? `Starts ${formatWhen(b.start)}` : `Waiting ${ago(b.createdAt)}`,
      title: b.productName ?? b.productId,
      meta: `${b.hotelName ?? 'a hotel'} · ${b.guests.adults + (b.guests.children ?? 0)} guests · ${fmtMoney(b.supplierAmount)}`,
      go: () => go(`/requests/${b.id}`),
    });
  }
  // B33 — a connection ask becomes a Connect task only once Today knows the
  // supplier has finished setting up; until /onboarding answers it is held
  // back, so a supplier mid-setup is never offered a Connect the API refuses.
  // Until then the whole list is still loading, not "Nothing needs you", which
  // would be a status shown before it is true. If /onboarding fails outright
  // the task does show. Then the server's refusal re-reads setup, and Today
  // becomes the checklist with the hotel shown waiting.
  const setupKnown = onboarding.data !== null || !!onboarding.error;
  for (const c of setupKnown ? pendingConns : []) {
    const name = c.name ?? c.hotelName ?? 'A hotel';
    tasks.push({
      key: `conn-${c.hotelAccountId ?? c.accountId}`,
      severity: 2,
      chipClass: 'soon',
      // N42: another hotel's invitation found by their number is the same task —
      // chipped "Also invited you", the person who invited leading the line.
      chipText: c.via === 'invited' ? ANOTHER_HOTEL.alsoInvited : ANOTHER_HOTEL.wants,
      title: name,
      // The phone's lines, here too (design review CONSIDER 7): what the hotel
      // sees, the trip, and "once they list your trip" unless Accept lists it.
      meta: [
        c.via === 'invited' ? ANOTHER_HOTEL.invitedToo(name, c.invitedBy) : '',
        c.via ? ANOTHER_HOTEL.sees(name) : CONNECT_TASK_META,
        c.trip ? tripPreview(name, c.trip) : '',
        c.via && !c.trip ? ACCEPT_LINE : '',
      ]
        .filter(Boolean)
        .join(' '),
      connection: c,
    });
  }
  // N42: a live hotel's trip still to answer — keep theirs, or add the hotel's.
  for (const c of setupKnown ? (connections.data?.items ?? []).filter((x) => x.status === 'active' && x.trip?.open) : []) {
    const name = c.name ?? c.hotelName ?? 'A hotel';
    tasks.push({
      key: `trip-${c.hotelAccountId ?? c.accountId}`,
      severity: 4,
      chipClass: 'waiting',
      chipText: TRIP_SUGGESTION.chip,
      title: TRIP_SUGGESTION.title(name, c.trip!),
      meta: TRIP_SUGGESTION.line(c.trip!),
      trip: { c, s: c.trip! },
    });
  }
  tasks.sort((a, b) => a.severity - b.severity);
  // One primary per view (design review SHOULD 4): the first hotel's Accept.
  const firstAsk = tasks.find((t) => t.connection);
  if (firstAsk) firstAsk.primary = true;

  // A supplier still setting up gets the first-run onboarding home (canon 11 E2
  // step 6), not the empty task list — the checklist that opens them for work.
  if (onboarding.data?.status === 'onboarding') {
    return (
      <OnboardingHome
        ob={onboarding.data}
        waiting={pendingConns}
        answered={answeredLines}
        onAnswer={answer}
        afterFinish={async () => {
          // Connections first: Finish has just made the invitation's hotel live,
          // and the ordinary Today must not open on its stale pending row — a
          // Connect the server would answer with "nothing to approve".
          await connections.reload();
          await onboarding.reload();
        }}
        afterKeep={setKept}
      />
    );
  }

  // B35 — whether each live hotel can book them now is the API's standing.
  const live = (connections.data?.items ?? []).filter((c) => c.status === 'active');
  const canBook = live.filter((c) => standingOf(c) === 'bookable').length;
  // The first Today: no request or job yet. It names each hotel and says
  // whether it can book them now (canon 11 step 6, only when it is true).
  const firstToday = items !== null && items.length === 0;

  return (
    <div>
      {/* Until setup is known this could still turn into the checklist, so the
          hero claims nothing yet, as on the phone. */}
      <InkHero
        eyebrow="Today"
        figure={items === null || !setupKnown ? '…' : jobsToday.length}
        unit={jobsToday.length === 1 ? 'job' : 'jobs'}
        explain={
          items === null || !setupKnown
            ? ' '
            : jobsToday[0]
              ? `Next: ${jobsToday[0].productName} at ${jobsToday[0].start.slice(11, 16)} · ${jobsToday[0].guests.adults + (jobsToday[0].guests.children ?? 0)} guests`
              : firstToday && canBook > 0
                ? canBookNow(canBook)
                : 'No departures today.'
        }
      />

      {/* Accepting makes a hotel live, not bookable — it has listed none of
          your trips yet. The confirmation says what changed, once; the
          standing below says who has the next step (B35). */}
      {connectedName && (
        <Panel style={{ marginBottom: 'var(--space-lg)' }}>
          <span style={{ color: 'var(--color-success)' }}>{connectedWith(connectedName)}</span>
        </Panel>
      )}
      {/* N42 — a Not now just said, with its undo; what a trip answer did. */}
      {answeredLines}
      {tripSaid.length > 0 && (
        <Panel style={{ marginBottom: 'var(--space-lg)' }}>
          {tripSaid.map((line) => (
            <p key={line} className="hint" role="status" style={{ margin: 'var(--space-xs) 0' }}>
              {line}
            </p>
          ))}
        </Panel>
      )}

      {items === null || !setupKnown ? (
        error ? <p className="error-line">{error}</p> : <Loading />
      ) : tasks.length === 0 ? (
        // Nothing to do — and the empty state says why (B35): no hotel connected
        // yet, a hotel that has not listed a trip (its step, on their screen), a
        // season not open yet, or truly nothing. The phone's N19 line, here too.
        // Only this state waits for the hotels' read; a task list never does.
        connections.data === null ? (
          connections.error ? (
            // Hotels never read: claim nothing about them.
            <div className="empty">
              <strong>{HOTELS_UNREAD.title}</strong>
              <p>{HOTELS_UNREAD.line}</p>
            </div>
          ) : (
            <Loading />
          )
        ) : (
          <StandingEmpty live={live} asking={pendingConns} firstToday={firstToday} />
        )
      ) : (
        <Panel title="Your list" meta={`${tasks.length} to do`} flush>
          <table>
            <tbody>
              {tasks.map((t) => (
                <TaskRow
                  key={t.key}
                  task={t}
                  onConnected={async (name) => {
                    setConnectedName(name);
                    await connections.reload();
                  }}
                  onAnswer={answer}
                  onTripAnswered={async (said) => {
                    setTripSaid((s) => [...s, said]);
                    await connections.reload();
                  }}
                  onSetupNotFinished={() => void onboarding.reload()}
                />
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {upcoming.length > 0 && (
        <Panel title="Coming up" style={{ marginTop: 20 }} flush>
          <table>
            <tbody>
              {upcoming.map((b) => (
                <tr key={b.id}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">
                      {b.hotelName} · {b.guests.adults + (b.guests.children ?? 0)} guests
                    </span>
                  </td>
                  <td className="num money">{fmtMoney(b.supplierAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {/* After setup only — so not until /onboarding has answered: while it is
          still loading this Today could yet turn into the checklist (N41
          review). Below, the status is known not to be 'onboarding'. */}
      {onboarding.data && (
        <>
          {/* N41 — the desk points at the phone app, so a request can reach the
              phone, not only a text after 5 minutes. */}
          <PhoneAppPanel />

          {/* My trips after setup: a link, never a rail entry (DECISION-054, N39) —
              canon 11 step 6's "add another product" task. */}
          <Panel title={MY_TRIPS_LINK.title} style={{ marginTop: 'var(--space-xl)' }} flush>
            <table>
              <tbody>
                <tr className="click" onClick={() => go('/trips')}>
                  <td>
                    <span className="hint">{MY_TRIPS_LINK.line}</span>
                  </td>
                  <td className="num">
                    <a href="#/trips" className="open" onClick={(e) => e.preventDefault()}>
                      {MY_TRIPS_LINK.open}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}

function TaskRow({
  task,
  onConnected,
  onAnswer,
  onTripAnswered,
  onSetupNotFinished,
}: {
  task: Task;
  onConnected: (name: string) => Promise<void>;
  // N42: Not now beside Connect, and the answer to a hotel's trip.
  onAnswer: (kind: AnswerKind, c: Connection) => Promise<void>;
  onTripAnswered: (said: string) => Promise<void>;
  // The server said setup isn't finished (B33): Today re-reads it and becomes
  // the checklist, with this hotel shown waiting.
  onSetupNotFinished: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    if (!task.connection) return;
    const hotelId = task.connection.hotelAccountId ?? task.connection.accountId ?? '';
    const name = task.connection.name ?? task.connection.hotelName ?? 'The hotel';
    setBusy(true);
    setError('');
    try {
      await api('POST', `/connections/${hotelId}/accept`);
      await onConnected(name);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      if (e instanceof ApiError && e.code === 'setup_not_finished') onSetupNotFinished();
    }
  };

  // A live hotel's offered trip: its own row, with the new trip's name in it.
  if (task.trip) {
    return (
      <TripRow chipClass={task.chipClass} chipText={task.chipText} title={task.title} meta={task.meta} c={task.trip.c} s={task.trip.s} onAnswered={onTripAnswered} />
    );
  }
  const name = task.connection ? task.connection.name ?? task.connection.hotelName ?? 'The hotel' : '';
  return (
    <tr className={task.go ? 'click' : ''} onClick={task.go}>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span className={`uchip ${task.chipClass}`}>{task.chipText}</span>
      </td>
      <td {...(task.connection ? { id: rowFocusId(task.connection), tabIndex: -1 } : {})}>
        {task.title}
        <br />
        <span className="hint">{task.meta}</span>
        {error && <p className="error-line" role="alert">{error}</p>}
      </td>
      <td className="num" style={{ whiteSpace: 'nowrap' }}>
        {task.connection ? (
          <>
            {/* Accept and Not now side by side — one primary in the view
                (DECISION-080 §6; design review SHOULD 4). */}
            <button className={task.primary ? 'btn' : 'btn secondary'} aria-label={ANOTHER_HOTEL.acceptLabel(name)} disabled={busy} onClick={accept}>
              {busy ? ANOTHER_HOTEL.accepting : ANOTHER_HOTEL.accept}
            </button>
            {task.connection.via !== 'invitation' && <AnswerButton kind="not-now" c={task.connection} onAnswer={onAnswer} />}
          </>
        ) : (
          <span className="open">Answer →</span>
        )}
      </td>
    </tr>
  );
}

// "Get requests on your phone" (N41): the phone app's link on this environment
// (config.ts appUrl), to open on the phone — in a read-only field that can be
// selected, beside a Copy that says what it did. A panel of Today's own, after
// setup only (the first run is the checklist above).
function PhoneAppPanel() {
  const link = config().appUrl;
  const [said, setSaid] = useState<'' | 'copied' | 'failed'>('');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setSaid('copied');
    } catch {
      setSaid('failed');
    }
  };
  return (
    <Panel title={PHONE_APP.title} style={{ marginTop: 'var(--space-xl)' }}>
      <p className="hint" style={{ margin: 0 }}>{PHONE_APP.line}</p>
      <label htmlFor="phone-app-link" style={{ marginTop: 'var(--space-lg)' }}>
        {PHONE_APP.label}
      </label>
      <div className="copy-row">
        <input id="phone-app-link" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="btn secondary" onClick={copy}>
          {said === 'copied' ? PHONE_APP.copied : PHONE_APP.copy}
        </button>
      </div>
      {said === 'failed' && (
        <p className="error-line" role="alert">
          {PHONE_APP.failed}
        </p>
      )}
      <p className="hint" style={{ marginTop: 'var(--space-md)', marginBottom: 0 }}>
        {PHONE_APP.until}
      </p>
    </Panel>
  );
}

function StandingEmpty({ live, asking, firstToday }: { live: Connection[]; asking: Connection[]; firstToday: boolean }) {
  const st = todayStanding(live, asking, firstToday);
  return (
    <div className="empty" role="status">
      <strong>{st.title}</strong>
      {st.lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

// ── An account someone else set up (DECISION-080 §9) ─────────────────────────
// A state of Today's first run, as on the phone — not a new screen. It opens on
// what was set up, PRICE FIRST — the supplier's own figure (R2), which only
// their yes commits (principle 6) — then their business and their days, each a
// row into its registered Form (My trips, Business profile, Availability), and
// one primary: Keep, their own write (POST /v1/onboarding/review). After it the
// ordinary checklist takes over with whatever is still missing, then Finish.
function SetUpForYou({ onKept }: { onKept: (state: Onboarding) => void }) {
  const products = useLoad<{ items: Product[] }>('/products');
  const pattern = useLoad<{ pattern: AvailabilityPattern | null }>('/availability/pattern');
  const account = useLoad<Account>('/account');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const trips = products.data?.items ?? null;
  const days = pattern.data?.pattern?.days ?? null;
  const first = trips?.[0];
  const w = SET_UP_FOR_YOU;
  // Keep needs everything that was set up on screen first. A read that failed
  // is said, with a way to try again — never a spinner that never ends, and
  // never a grey Keep with no reason.
  const loaded = trips !== null && pattern.data !== null;
  const loadFailed = (trips === null && !!products.error) || (pattern.data === null && !!pattern.error);
  const retry = () => {
    if (products.error) void products.reload();
    if (pattern.error) void pattern.reload();
  };

  const keep = async () => {
    setBusy(true);
    setError('');
    try {
      onKept(await api<Onboarding>('POST', '/onboarding/review'));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  // Each Change is a real link, so it is reachable from the keyboard; the row
  // stays clickable as the rest of the desk's rows are.
  const change = (to: string) => (
    <a className="open" href={`#${to}`} onClick={(e) => e.stopPropagation()}>
      {w.change}
    </a>
  );

  return (
    <div>
      <InkHero
        eyebrow={w.eyebrow}
        figure={first ? fmtMoney(first.supplierPrice) : trips === null && !products.error ? '…' : '—'}
        unit={first ? (first.per === 'guest' ? 'a guest' : 'a trip') : undefined}
        explain={first ? first.name : trips === null ? ' ' : w.noTrips}
      />

      <Panel title={w.title}>
        <p className="hint" style={{ margin: 0 }}>{w.lede}</p>
      </Panel>

      {loadFailed && (
        <Panel style={{ marginTop: 'var(--space-xl)' }}>
          <p className="error-line" role="alert">{w.loadFailed}</p>
          <button className="btn secondary" onClick={retry}>
            {w.retry}
          </button>
        </Panel>
      )}

      <Panel title={w.trips} flush style={{ marginTop: 'var(--space-xl)' }}>
        {trips === null ? (
          products.error ? null : <Loading />
        ) : trips.length === 0 ? (
          <div className="card-body hint">{w.noTrips}</div>
        ) : (
          <table>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id} className="click" onClick={() => go(`/trips/${t.id}`)}>
                  {/* Price first: the figure is what they are paid, theirs to keep. */}
                  <td className="num money" style={{ whiteSpace: 'nowrap' }}>
                    {fmtMoney(t.supplierPrice)} {t.per === 'guest' ? 'a guest' : 'a trip'}
                  </td>
                  <td>
                    {t.name}
                    <br />
                    <span className="hint">
                      {[t.durationHours ? `${t.durationHours} hours` : null, t.maxGuests ? `up to ${t.maxGuests}` : null]
                        .filter(Boolean)
                        .join(' · ') || 'No length or group size set'}
                    </span>
                  </td>
                  <td className="num">{change(`/trips/${t.id}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title={w.business} flush style={{ marginTop: 'var(--space-xl)' }}>
        <table>
          <tbody>
            <tr className="click" onClick={() => go('/profile')}>
              <td>
                {account.data?.name ?? '…'}
                <br />
                <span className="hint">
                  {[account.data?.location?.place, account.data?.languages?.join(', ')].filter(Boolean).join(' · ') ||
                    'Where you leave from and your languages come next.'}
                </span>
              </td>
              <td className="num">{change('/profile')}</td>
            </tr>
          </tbody>
        </table>
      </Panel>

      <Panel title={w.days} flush style={{ marginTop: 'var(--space-xl)' }}>
        <table>
          <tbody>
            <tr className="click" onClick={() => go('/availability')}>
              <td>{pattern.data === null ? (pattern.error ? '—' : '…') : days && days.length ? summariseDays(days) : w.noDays}</td>
              <td className="num">{change('/availability')}</td>
            </tr>
          </tbody>
        </table>
      </Panel>

      {error && <p className="error-line" role="alert">{error}</p>}

      <div className="form-foot">
        <span className="hint" style={{ flex: 1, alignSelf: 'center' }}>
          {loadFailed ? w.keepWaits : w.keepHint}
        </span>
        <button className="btn" onClick={keep} disabled={busy || !loaded} style={{ flex: 'none' }}>
          {busy ? w.keeping : w.keep}
        </button>
      </div>
    </div>
  );
}
